import { useEffect, useState } from "react";

import type {
	ColumnDef,
	OnChangeFn,
	PaginationOptions,
	PaginationState,
	Row,
	SortingState
} from "@tanstack/react-table";

import { dateRangeFilter, fuzzyFilter } from "@/components/data-table/filters";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable
} from "@tanstack/react-table";

// Define TabOption type explicitly
export interface TabOption {
	value: string;
	label: string;
}

export const DEFAULT_PAGE_INDEX = 0;
export const DEFAULT_PAGE_SIZE = 10;

export interface GenericDataTableProps<T extends Record<string, any>> {
	// Required props
	data: T[];
	columns: ColumnDef<T>[];

	// Pagination related props
	pagination: PaginationState;
	paginationOptions: Pick<PaginationOptions, "onPaginationChange" | "rowCount">;

	// Sorting related props
	sorting: SortingState;
	onSortingChange: OnChangeFn<SortingState>;

	// Filter related props
	filters?: Record<string, any>;
	onSearchChange: (search: string) => void;
	searchValue?: string;
	onFilterChange?: (dataFilters: Partial<T>) => void;

	// Tab related props
	tabOptions: TabOption[];
	activeTab: string;
	onTabChange: (tab: string) => void;
	filterField?: keyof T;

	// UI related props
	title?: string;
	isLoading?: boolean;
	description?: string;
	headerShown?: boolean;

	// Event handlers
	handleRowClick?: (row: Row<T>) => void;
}

export function GenericDataTable<T extends Record<string, any>>({
	data,
	sorting,
	activeTab,
	handleRowClick,
	onTabChange,
	columns,
	headerShown,
	paginationOptions,
	pagination,
	onFilterChange,
	onSortingChange,
	onSearchChange,
	searchValue,
	tabOptions,
	filterField,
	title,
	description = "",
	isLoading = true
}: GenericDataTableProps<T>) {
	const [globalFilter, setGlobalFilter] = useState("");

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onGlobalFilterChange: setGlobalFilter,
		filterFns: {
			fuzzy: fuzzyFilter,
			dateRange: dateRangeFilter
		},
		globalFilterFn: fuzzyFilter,
		state: { pagination, sorting, globalFilter },
		onSortingChange,
		...paginationOptions,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true
	});

	useEffect(() => {
		if (onFilterChange && filterField) {
			onFilterChange({ [filterField]: activeTab } as Partial<T>);
		}
	}, [activeTab, filterField, onFilterChange]);

	return (
		<div className="w-full py-4 sm:py-6">
			<Card>
				{(title || description) && (
					<CardHeader>
						{title && <CardTitle>{title}</CardTitle>}
						{description && <CardDescription>{description}</CardDescription>}
					</CardHeader>
				)}
				<CardContent className="p-3 sm:p-6">
					<Tabs defaultValue={tabOptions[0].value} value={activeTab} onValueChange={onTabChange}>
						<div className="-mx-1 overflow-x-auto px-1">
							<TabsList className="flex h-auto w-max min-w-full justify-start gap-4 rounded-none bg-transparent p-0 sm:gap-6">
								{tabOptions.map(tab => (
									<TabsTrigger
										key={tab.value}
										value={tab.value}
										className="shrink-0 rounded-none border-b-2 border-transparent px-0 pb-1 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
									>
										{tab.label}
									</TabsTrigger>
								))}
							</TabsList>
						</div>

						<div className="flex items-center py-4">
							<Input
								id="table-search"
								name="table-search"
								type="search"
								autoComplete="off"
								placeholder="Search..."
								value={searchValue ?? globalFilter}
								onChange={(e) => {
									setGlobalFilter(e.target.value);
									onSearchChange(e.target.value);
								}}
								className="w-full max-w-sm"
							/>
						</div>

						<div className="overflow-x-auto rounded-md border">
							<Table>
								<TableHeader className={cn({ hidden: !headerShown })}>
									{table.getHeaderGroups().map(headerGroup => (
										<TableRow key={headerGroup.id}>
											{headerGroup.headers.map(header => (
												<TableHead key={header.id}>
													{header.isPlaceholder
														? null
														: flexRender(header.column.columnDef.header, header.getContext())}
												</TableHead>
											))}
										</TableRow>
									))}
								</TableHeader>
								<TableBody>
									<TableRow className={cn("border-none", { hidden: !isLoading })}>
										<TableCell
											colSpan={columns.length}
											className="absolute inset-0 bg-black/15 flex items-center justify-center rounded-md"
										>
											<div className="animate-spin rounded-full size-8 border-b-2 border-primary"></div>
										</TableCell>
									</TableRow>
									{
										table.getRowModel().rows?.length
											? (
													table.getRowModel().rows.map(row => (
														<TableRow
															className={handleRowClick ? "cursor-pointer" : ""}
															key={row.id}
															data-state={row.getIsSelected() && "selected"}
															onClick={() => handleRowClick ? handleRowClick(row) : undefined}
														>
															{row.getVisibleCells().map(cell => (
																<TableCell key={cell.id}>
																	{flexRender(cell.column.columnDef.cell, cell.getContext())}
																</TableCell>
															))}
														</TableRow>
													))
												)
											: (
													<TableRow>
														<TableCell colSpan={columns.length} className="h-24 text-center">
															{isLoading ? "" : "No results."}
														</TableCell>
													</TableRow>
												)
									}
								</TableBody>
							</Table>
						</div>

						<div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
							<div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
								<span>
									{`Page ${table.getState().pagination.pageIndex + 1} of ${Math.max(1, table.getPageCount())}`}
								</span>
								{" "}
								<Select
									value={table.getState().pagination.pageSize.toString()}
									onValueChange={(value) => {
										table.setPageSize(Number(value));
									}}
								>
									<SelectTrigger className="w-fit">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{[10, 20, 30, 40, 50].map(pageSize => (
												<SelectItem key={pageSize} value={pageSize.toString()}>
													{`Show ${pageSize}`}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => table.previousPage()}
									disabled={!table.getCanPreviousPage()}
								>
									Previous
								</Button>
								<Button
									variant="outline"
									size="sm"
									onClick={() => table.nextPage()}
									disabled={!table.getCanNextPage()}
								>
									Next
								</Button>
							</div>
						</div>
					</Tabs>
				</CardContent>
			</Card>
		</div>
	);
}
